import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MIconMenu } from 'library';
import SubdirectoryArrowLeftRoundedIcon from '@mui/icons-material/SubdirectoryArrowLeftRounded';
import { useUserRolePermissions } from 'hooks';
import { func, number, string } from 'prop-types';
import { format } from 'date-fns';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import { distributionStatusTooltips, isEditableStatus } from './utils';
import { DISTRIBUTIONS_DRAWER_MODE } from './DistributionsDrawer/DistributionsDrawer';
import { OLD_STATUS_CHIP } from './components/OldStatusChip';
import StatusChip from './components/StatusChip';

const titleStyling = {
  fontWeight: 400,
};

/* eslint-disable sort-keys-fix/sort-keys-fix */
const gridStyle = {
  display: 'grid',
  gridGap: 8,
  gridTemplateAreas: {
    xs: `
      'main action'
      'chaining product'
      'origin date'
      'volume booked'
      `,
    lg: `'main hr status chaining origin product volume booked date action'`,
  },
  gridTemplateColumns: {
    xs: '1fr 1fr',
    lg: '1.25fr 0.2fr repeat(2, 0.7fr) repeat(5, 0.75fr) 0.1fr',
  },
  gridTemplateRows: { xs: 'repeat(4, auto)', lg: '1fr' },
};
/* eslint-enable sort-keys-fix/sort-keys-fix */

const DistributionsCard = ({
  bookedVolume,
  chaining,
  requestedDate,
  distributionId,
  fromPage,
  inventoryOwnerName,
  locationName,
  shipperName,
  onDelete,
  parentShipmentId,
  productName,
  status,
  volume,
  setSelectedDistribution,
}) => {
  const { isProductShippingManage } = useUserRolePermissions();

  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const navigate = useNavigate();

  const viewInventoryOwnerMenu = [
    {
      label: 'View Distribution',
      onClick: () =>
        setSelectedDistribution({
          distributionId,
          mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
        }),
      value: 'view',
    },
  ];

  const viewParentMenu = [
    {
      label: 'View Parent Shipment',
      onClick: () =>
        navigate(`../batches/shipment/${parentShipmentId}`, { state: { from: fromPage } }),
      value: 'view',
    },
  ];

  const manageMenu = [
    {
      label: 'Edit Distribution',
      onClick: () =>
        setSelectedDistribution({
          distributionId,
          mode: DISTRIBUTIONS_DRAWER_MODE.EDIT,
        }),
      value: 'edit',
    },
    {
      label: 'Delete Distribution',
      onClick: () => {
        onDelete('distribution', distributionId);
      },
      value: 'delete',
    },
  ];

  const isShipper = affiliates.some(({ customerNameShort }) =>
    R.equals(customerNameShort, shipperName)
  );

  const isSameUserShipper = currentAffiliate?.customerNameShort === shipperName;
  const isSameUserShipperInvOwner =
    currentAffiliate?.customerNameShort === inventoryOwnerName &&
    inventoryOwnerName === shipperName;
  const isShowParentMenu = isShipper || isSameUserShipper || isSameUserShipperInvOwner;

  const isViewParentStatus = [
    OLD_STATUS_CHIP.CANCELED,
    OLD_STATUS_CHIP.SHIPPED,
    OLD_STATUS_CHIP.EXPIRED,
    OLD_STATUS_CHIP.IN_TRANSIT,
    OLD_STATUS_CHIP.UNCONFIRMED,
    OLD_STATUS_CHIP.CANNOT_BOOK,
    OLD_STATUS_CHIP.PROCESSING,
  ].includes(status);
  const menuItems = R.all(R.equals(R.T()), [
    isProductShippingManage,
    isShipper,
    isEditableStatus(status),
  ])
    ? fromPage !== 'Parent Shipment'
      ? R.unnest([viewInventoryOwnerMenu, viewParentMenu, manageMenu])
      : R.concat(viewInventoryOwnerMenu, manageMenu)
    : isViewParentStatus && isShowParentMenu && fromPage !== 'Parent Shipment'
    ? R.unnest([viewInventoryOwnerMenu, viewParentMenu])
    : viewInventoryOwnerMenu;

  return (
    <Card sx={{ '&:last-child': { mb: 0 }, mb: 2 }}>
      <CardContent
        sx={{
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          p: { xs: 2, lg: 3 },
        }}
      >
        <Box sx={gridStyle}>
          {/* eslint-disable-next-line sort-keys-fix/sort-keys-fix */}
          <Stack spacing={0.5} sx={{ gridArea: 'main', gridColumn: 1, mb: { xs: 1, lg: 0 } }}>
            <Typography sx={titleStyling} variant="caption">
              #{distributionId}
            </Typography>
            <Typography sx={{ fontWeight: 700 }} variant="body1">
              {inventoryOwnerName}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <SubdirectoryArrowLeftRoundedIcon
                sx={{ color: 'graphic.dark', fontSize: 16, transform: 'rotate(90deg)' }}
              />
              <Typography sx={{ fontWeight: 400, pt: '2px' }} variant="caption">
                {shipperName}
              </Typography>
            </Stack>
            {/* eslint-disable-next-line sort-keys-fix/sort-keys-fix */}
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Tooltip
                enterTouchDelay={10}
                placement="top"
                title={distributionStatusTooltips[status] || ''}
              >
                <StatusChip status={status} />
              </Tooltip>
            </Box>
          </Stack>
          <Box
            sx={{
              borderLeft: 1,
              borderLeftColor: 'graphic.light',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              display: { xs: 'none', lg: 'block' },
              gridArea: 'hr',
              gridColumn: 2,
            }}
          />
          {/* eslint-disable-next-line sort-keys-fix/sort-keys-fix */}
          <Stack spacing={0.5} sx={{ display: { xs: 'none', lg: 'flex' }, gridArea: 'status' }}>
            <Typography
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              sx={{ ...titleStyling, display: { xs: 'none', lg: 'inline' } }}
              variant="caption"
            >
              Status
            </Typography>
            <Tooltip
              enterTouchDelay={10}
              placement="top"
              title={distributionStatusTooltips[status] || ''}
            >
              <StatusChip status={status} />
            </Tooltip>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'chaining' }}>
            <Typography sx={titleStyling} variant="caption">
              Shipment Chain
            </Typography>
            <Typography variant="body2">{chaining} Recipient(s)</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'origin' }}>
            <Typography sx={titleStyling} variant="caption">
              Location
            </Typography>
            <Typography variant="body2">{locationName}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'product' }}>
            <Typography sx={titleStyling} variant="caption">
              Product
            </Typography>
            <Typography variant="body2">{productName}</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'volume' }}>
            <Typography sx={titleStyling} variant="caption">
              Volume
            </Typography>
            <Typography variant="body2">{formatNumberWithoutDecimals(volume)} bbl</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'booked' }}>
            <Typography sx={titleStyling} variant="caption">
              Booked Volume
            </Typography>
            <Typography variant="body2">{formatNumberWithoutDecimals(bookedVolume)} bbl</Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ gridArea: 'date' }}>
            <Typography sx={titleStyling} variant="caption">
              Requested Date
            </Typography>
            <Typography variant="body2">
              {format(Date.parse(requestedDate), 'MMMM d, yyyy')}
            </Typography>
          </Stack>
          <Box sx={{ gridArea: 'action', justifySelf: 'end' }}>
            <MIconMenu items={menuItems} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

DistributionsCard.propTypes = {
  bookedVolume: number.isRequired,
  chaining: number.isRequired,
  distributionId: number.isRequired,
  fromPage: string.isRequired,
  inventoryOwnerName: string.isRequired,
  locationName: string.isRequired,
  onDelete: func.isRequired,
  parentShipmentId: number.isRequired,
  productName: string.isRequired,
  requestedDate: string.isRequired,
  setSelectedDistribution: func.isRequired,
  shipperName: string.isRequired,
  status: string.isRequired,
  volume: number.isRequired,
};

export default DistributionsCard;
